import {
  CreditCard,
  ChartBar,
  SignOut,
  ChatCircleText,
  FileText,
  Shield,
  Trash,
  Globe,
  CircleHalf,
  Tag,
  Crown,
  Key,
} from '@phosphor-icons/react';

export interface AccountMenuItem {
  href: string;
  labelKey: string;
  icon: React.ComponentType<{ className?: string; weight?: 'regular' | 'fill' }>;
  /** Special item type for non-link actions */
  action?: 'logout';
}

export interface AccountMenuSection {
  titleKey?: string;
  items: AccountMenuItem[];
}

/**
 * Single source of truth for account settings menu structure.
 * Used by both the mobile account page and the desktop sidebar.
 */
/**
 * Hrefs excluded from prev/next pager navigation in the settings header.
 * Mirrors Apple's `AccountMenuItem.navigableItems` filter — privacy/terms
 * are external docs, delete is destructive, logout is an action.
 */
const NON_NAVIGABLE_HREFS: ReadonlySet<string> = new Set([
  '/account/privacy',
  '/account/terms',
  '/account/delete',
]);

export const accountMenuSections: AccountMenuSection[] = [
  {
    items: [
      { href: '/account/tokens', labelKey: 'tokenBalance', icon: Crown },
    ],
  },
  {
    titleKey: 'sections.settings',
    items: [
      { href: '/account/language', labelKey: 'settings.language', icon: Globe },
      { href: '/account/appearance', labelKey: 'settings.appearance', icon: CircleHalf },
      // Branding (white-label) is shown to EVERYONE for discoverability, even
      // users without the `white_label` entitlement (Pro Plan / grandfathered
      // Pro). The Branding page itself locks the toggles and opens the Pass
      // paywall, so exposing the row acts as an upsell surface rather than a
      // dead end. Removing this row would hide the feature from prospects.
      { href: '/account/branding', labelKey: 'settings.branding', icon: Tag },
      // Agent API keys, self-service (docs/agent-api/PLAN.md phase 3.5). Shown
      // to everyone for the same reason branding is: the page itself states the
      // paid-only rule with API text, so the row is a discovery surface rather
      // than a dead end. Mirrored on Apple and Android.
      { href: '/account/agent-keys', labelKey: 'settings.agentKeys', icon: Key },
    ],
  },
  {
    titleKey: 'sections.support',
    items: [
      { href: '/account/feedback', labelKey: 'support.contactSupport', icon: ChatCircleText },
      { href: '/account/privacy', labelKey: 'support.privacyPolicy', icon: Shield },
      { href: '/account/terms', labelKey: 'support.termsOfService', icon: FileText },
    ],
  },
  {
    titleKey: 'sections.account',
    items: [
      { href: '/account/purchase-history', labelKey: 'subscriptionActions.purchaseHistory', icon: CreditCard },
      { href: '/account/usage-stats', labelKey: 'subscriptionActions.usageStatistics', icon: ChartBar },
      { href: '', labelKey: 'logout', icon: SignOut, action: 'logout' },
      { href: '/account/delete', labelKey: 'deleteAccount.title', icon: Trash },
    ],
  },
];

/**
 * Flat list of menu items participating in prev/next pager navigation.
 * Excludes logout action, privacy/terms (external docs), and delete (destructive).
 */
export const navigableAccountItems: AccountMenuItem[] = accountMenuSections
  .flatMap((section) => section.items)
  .filter((item) => item.action !== 'logout' && !NON_NAVIGABLE_HREFS.has(item.href));

/**
 * All linkable account menu items (excludes only the logout action).
 * Used by the layout header to resolve a page title from the current
 * pathname so each settings sub-page renders the same title shown in the
 * sidebar — matches Apple's `AccountMenuItem.displayName` lookup.
 */
export const allAccountLinkItems: AccountMenuItem[] = accountMenuSections
  .flatMap((section) => section.items)
  .filter((item) => item.action !== 'logout' && item.href.length > 0);
